import { useMutation } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { useTranslation } from 'react-i18next';

interface PaymentProducts {
  essentielQuantity?: number;
  integralQuantity?: number;
  packAICredits8?: number;
}

export function useCreatePaymentLink() {
  const { notifyError } = useNotification();
  const { t } = useTranslation(['userSettings']);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (products: PaymentProducts) => {
      // Transform quantity fields for school subscriptions
      const transformedProducts = {
        essentiel: products.essentielQuantity,
        integral: products.integralQuantity,
        packAICredits8: products.packAICredits8,
      };

      // Remove undefined fields
      Object.keys(transformedProducts).forEach(key => {
        if (
          transformedProducts[key as keyof typeof transformedProducts] ===
          undefined
        ) {
          delete transformedProducts[key as keyof typeof transformedProducts];
        }
      });

      return request('/billing/payment-link', {
        method: 'POST',
        body: {
          products: transformedProducts,
        },
      });
    },
    onSuccess: (data, variables) => {
      // Handle both response formats: { paymentLinkUrl } and { url }
      let redirectUrl = data?.paymentLinkUrl || data?.url;

      // For AI pack purchases, append productType to success URL
      if (redirectUrl && variables.packAICredits8) {
        const url = new URL(redirectUrl);
        // Find and update the success_url parameter
        const successUrl = url.searchParams.get('success_url');
        if (successUrl) {
          const successUrlObj = new URL(successUrl);
          successUrlObj.searchParams.set('productType', 'packAICredits8');
          url.searchParams.set('success_url', successUrlObj.toString());
          redirectUrl = url.toString();
        }
      }

      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    },
    onError: () => {
      notifyError({
        title: t('userSettings:notifications.checkoutLinkGenerationFailed'),
      });
    },
  });

  return {
    mutateAsync,
    isPending,
  };
}
